/*
 * Settings: the partner's profile (name, email and code are Wealthoak's to
 * change; phone, company and city are theirs, PATCH /profile), the agreed
 * commission terms, a password, and sign out.
 */

import { useState } from "react";
import { auth, errorMessage, partner } from "../api/client";
import { useAuth, useMe } from "../auth/AuthContext";
import type { Notify } from "./bits";
import { accountStatus, Status } from "./bits";
import { fmtDate, ordinal, rateLabel, slabLabel } from "./format";
import { LogOut } from "./icons";
import "./settings.css";

const MIN_PASSWORD = 10;
const PARTNERS_EMAIL = "info@wealthoak.ai";

export default function SettingsWorkspace({ notify }: { notify: Notify }) {
  const me = useMe();
  const { signOut } = useAuth();
  const st = me.status === "pending" ? { label: "Awaiting approval", tone: "amber" as const } : accountStatus(me.status);

  return (
    <div className="set">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Settings</p>
          <h1>{me.full_name}</h1>
          <p className="page-heading__sub">
            Partner {me.code} · joined {fmtDate(me.joined_at)}
          </p>
        </div>
      </section>

      <section className="card set-card" aria-labelledby="set-profile">
        <header className="set-card__head">
          <h2 id="set-profile">Profile</h2>
          <Status tone={st.tone}>{st.label}</Status>
        </header>
        <dl className="kv">
          <div>
            <dt>Name</dt>
            <dd>{me.full_name}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{me.email}</dd>
          </div>
          <div>
            <dt>Partner code</dt>
            <dd className="mono">{me.code}</dd>
          </div>
          <div>
            <dt>Referral link</dt>
            <dd className="mono set-link">{me.referral_link}</dd>
          </div>
          <div>
            <dt>Add-user credits</dt>
            <dd>
              <strong>{me.user_credits.available}</strong> available · {me.user_credits.earned} earned through your
              link{me.user_credits.pending ? ` · ${me.user_credits.pending} in review` : ""}
              {me.user_credits.used ? ` · ${me.user_credits.used} used` : ""}
            </dd>
          </div>
        </dl>
        <ProfileForm notify={notify} />
        <p className="set-foot">
          To change your name or email, write to <a href={`mailto:${PARTNERS_EMAIL}`}>{PARTNERS_EMAIL}</a>.
        </p>
      </section>

      <section className="card set-card" aria-labelledby="set-terms">
        <header className="set-card__head">
          <h2 id="set-terms">Commission terms</h2>
        </header>
        <dl className="kv">
          <div>
            <dt>Commission rate</dt>
            <dd>
              {me.commission_rate ? (
                <>
                  <strong>{rateLabel(me.commission_rate)}</strong> of each referred client&apos;s subscription fee,
                  excluding GST
                  {me.commission_program && (
                    <>
                      . Tier {me.commission_program.tier}:{" "}
                      {me.commission_program.slabs.map((s) => `${slabLabel(s)} ${rateLabel(s.rate)}`).join(", ")}
                    </>
                  )}
                </>
              ) : (
                "Not set yet"
              )}
            </dd>
          </div>
          {me.commission_program && (
            <div>
              <dt>Payout starts</dt>
              <dd>
                With your {ordinal(me.commission_program.activation_clients + 1)} client. Until then commission is
                recorded as In progress and released once you get there.
              </dd>
            </div>
          )}
          <div>
            <dt>Hold period</dt>
            <dd>{me.hold_days} days after the client pays, then withdrawable</dd>
          </div>
          <div>
            <dt>Your own subscription</dt>
            <dd>
              If you&apos;re also a Wealthoak client, you can pay its invoices from your wallet, with GST and TDS taken
              off as for a withdrawal. We find your subscription by your sign-in email; if it&apos;s under another
              address, ask Wealthoak to link it. Your own subscription never earns you commission.
            </dd>
          </div>
        </dl>
        <p className="set-foot">
          Your rate is agreed with Wealthoak. A change applies to future payments only; commission already earned keeps
          its rate.
        </p>
      </section>

      <PasswordCard notify={notify} />

      <section className="card set-card" aria-labelledby="set-signout">
        <header className="set-card__head">
          <h2 id="set-signout">Sign out</h2>
        </header>
        <p className="set-copy">Sign out of the partner panel on this device.</p>
        <div className="set-actions">
          <button className="button button--danger" onClick={() => void signOut()}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </section>
    </div>
  );
}

/* ---------- phone, company, city ---------- */

function ProfileForm({ notify }: { notify: Notify }) {
  const me = useMe();
  const { setMe } = useAuth();
  const [phone, setPhone] = useState(me.phone ?? "");
  const [company, setCompany] = useState(me.company_name ?? "");
  const [city, setCity] = useState(me.city ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only what changed is sent; a cleared field is sent as null.
  const changes: { phone?: string | null; company_name?: string | null; city?: string | null } = {};
  if (phone.trim() !== (me.phone ?? "")) changes.phone = phone.trim() || null;
  if (company.trim() !== (me.company_name ?? "")) changes.company_name = company.trim() || null;
  if (city.trim() !== (me.city ?? "")) changes.city = city.trim() || null;
  const dirty = Object.keys(changes).length > 0;
  const phoneBad = Boolean(phone.trim()) && !/^[+\d][\d\s-]{6,19}$/.test(phone.trim());

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || phoneBad) return;
    setBusy(true);
    setError(null);
    try {
      const next = await partner.updateProfile(changes);
      setMe(next);
      setPhone(next.phone ?? "");
      setCompany(next.company_name ?? "");
      setCity(next.city ?? "");
      notify("Profile saved.");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="set-form" onSubmit={save} noValidate>
      <div className="form-grid set-form__grid">
        <div className="field">
          <label className="field__label" htmlFor="pf-phone">Phone</label>
          <input
            id="pf-phone"
            className="input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            maxLength={20}
            aria-invalid={phoneBad}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98200 12345"
          />
          {phoneBad && <p className="field__error">Use digits, spaces or dashes.</p>}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="pf-company">Company</label>
          <input id="pf-company" className="input" autoComplete="organization" value={company} maxLength={200} onChange={(e) => setCompany(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="pf-city">City</label>
          <input id="pf-city" className="input" autoComplete="address-level2" value={city} maxLength={100} onChange={(e) => setCity(e.target.value)} />
        </div>
      </div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="form-actions">
        {dirty && (
          <button
            type="button"
            className="button button--secondary"
            disabled={busy}
            onClick={() => {
              setPhone(me.phone ?? "");
              setCompany(me.company_name ?? "");
              setCity(me.city ?? "");
              setError(null);
            }}
          >
            Discard
          </button>
        )}
        <button type="submit" className="button button--primary" disabled={busy || !dirty || phoneBad}>
          {busy ? "Saving…" : "Save profile"}
        </button>
      </div>
    </form>
  );
}

/* ---------- password ---------- */

function PasswordCard({ notify }: { notify: Notify }) {
  const me = useMe();
  const { setMe } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const short = pw.length < MIN_PASSWORD;
  const mismatch = confirm !== pw;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (short || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await auth.setPassword(pw);
      setMe({ ...me, has_password: true });
      setOpen(false);
      setPw("");
      setConfirm("");
      setTouched(false);
      notify(me.has_password ? "Password changed." : "Password set. You can now sign in with it.");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card set-card" aria-labelledby="set-password">
      <header className="set-card__head">
        <h2 id="set-password">{me.has_password ? "Password" : "Set a password"}</h2>
      </header>
      <p className="set-copy">
        {me.has_password
          ? `Sign in with your password or a one-time code sent to ${me.email}.`
          : `You sign in with a one-time code sent to ${me.email}. Set a password to sign in without waiting for an email.`}
      </p>
      {open ? (
        <form onSubmit={save} noValidate>
          <div className="form-grid set-form__grid">
            <div className="field">
              <label className="field__label" htmlFor="pw-new">New password</label>
              <input
                id="pw-new"
                className="input"
                type="password"
                autoComplete="new-password"
                value={pw}
                minLength={MIN_PASSWORD}
                aria-invalid={touched && short}
                aria-describedby="pw-new-hint"
                onChange={(e) => setPw(e.target.value)}
                autoFocus
              />
              <p id="pw-new-hint" className={touched && short ? "field__error" : "field__hint"}>
                At least {MIN_PASSWORD} characters.
              </p>
            </div>
            <div className="field">
              <label className="field__label" htmlFor="pw-confirm">Confirm password</label>
              <input
                id="pw-confirm"
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                aria-invalid={touched && mismatch}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {touched && mismatch && <p className="field__error">The passwords don&apos;t match.</p>}
            </div>
          </div>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="form-actions">
            <button
              type="button"
              className="button button--secondary"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setPw("");
                setConfirm("");
                setTouched(false);
                setError(null);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="button button--primary" disabled={busy}>
              {busy ? "Saving…" : me.has_password ? "Change password" : "Set password"}
            </button>
          </div>
        </form>
      ) : (
        <div className="set-actions">
          <button className="button" onClick={() => setOpen(true)}>
            {me.has_password ? "Change password" : "Set a password"}
          </button>
        </div>
      )}
    </section>
  );
}
